import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import {
  ActivityLibraryFilters,
  fetchActivityLibrary,
  saveActivityToLibrary,
  addLibraryActivityToItinerary,
  deleteActivityFromLibrary,
} from '@/services/library.service';
import {
  LibraryActivityPayload,
  LibraryOwnerType,
} from '@/lib/library/models/library-activity.model';

export const ACTIVITY_LIBRARY_QUERY_KEY = 'activity-library';

const activityLibraryQueryKey = (filters: ActivityLibraryFilters = {}) => [
  ACTIVITY_LIBRARY_QUERY_KEY,
  filters.activityType ?? null,
  filters.tags ?? null,
  filters.search ?? null,
];

/**
 * Query TanStack per la lista della libreria personalizzata di attività,
 * filtrabile per tipo attività, tag e ricerca testuale.
 */
export const useActivityLibraryQuery = (filters: ActivityLibraryFilters = {}) => {
  return useQuery({
    queryKey: activityLibraryQueryKey(filters),
    queryFn: () => fetchActivityLibrary(filters),
  });
};

/**
 * Mutation per salvare (insert/update) un'attività nella libreria personale o di agenzia.
 * Invalida la query lista e mostra un toast di esito.
 */
export const useSaveActivityToLibrary = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({
      activity,
      ownerType,
      ownerId,
    }: {
      activity: LibraryActivityPayload;
      ownerType: LibraryOwnerType;
      ownerId: string;
    }) => saveActivityToLibrary(activity, ownerType, ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_LIBRARY_QUERY_KEY] });
      toast({
        title: t('library.saveSuccessTitle'),
        description: t('library.saveSuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error saving activity to library:', error);
      toast({
        title: t('common.error'),
        description: t('library.saveErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Mutation per eliminare una entry dalla libreria personale/di agenzia.
 * Invalida la query lista e mostra un toast di esito. La RLS DELETE lato server
 * resta l'unica fonte di verità sui permessi: questa mutation non fa altro
 * controllo di ownership, si limita a propagare l'eventuale errore.
 */
export const useDeleteActivityFromLibrary = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: (id: string) => deleteActivityFromLibrary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVITY_LIBRARY_QUERY_KEY] });
      toast({
        title: t('library.deleteSuccessTitle'),
        description: t('library.deleteSuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error deleting activity from library:', error);
      toast({
        title: t('common.error'),
        description: t('library.deleteErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};

/**
 * Mutation per copiare un'attività di libreria in un itinerario, in una data specifica.
 * Mostra un toast di esito. L'invalidation della lista attività dell'itinerario è a
 * carico degli hook dedicati del builder (fuori dallo scope di questo modulo dati).
 */
export const useAddLibraryActivityToItinerary = () => {
  const { toast } = useToast();
  const { t } = useI18n();

  return useMutation({
    mutationFn: ({
      libraryActivityId,
      itineraryId,
      date,
    }: {
      libraryActivityId: string;
      itineraryId: string;
      date: string;
    }) => addLibraryActivityToItinerary(libraryActivityId, itineraryId, date),
    onSuccess: () => {
      toast({
        title: t('library.addToItinerarySuccessTitle'),
        description: t('library.addToItinerarySuccessDescription'),
      });
    },
    onError: (error: unknown) => {
      console.error('Error adding library activity to itinerary:', error);
      toast({
        title: t('common.error'),
        description: t('library.addToItineraryErrorDescription'),
        variant: 'destructive',
      });
    },
  });
};
